import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  CircularProgress,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useSnackbar } from '../contexts/SnackbarContext.tsx';
import { useAuth } from '../contexts/AuthContext.tsx';
import { vendorsApi, Vendor, CreateVendorRequest } from '../services/vendorsService.ts';
import { formatPhoneNumber } from '../utils/formatPhone.ts';

const PAYMENT_TERMS_OPTIONS = ['15 Days', '30 Days', '45 Days', '60 Days', '90 Days'];
const STATUS_OPTIONS = ['Active', 'Inactive', 'Pending'];
const YES_NO_OPTIONS = ['Yes', 'No'];

const emptyForm: CreateVendorRequest = {
  companyName: '',
  vendorCategory: '',
  contactPerson: '',
  country: '',
  state: '',
  city: '',
  address: '',
  phone: '',
  email: '',
  website: '',
  productsSupplied: '',
  productCategory: '',
  manufacturerDistributor: '',
  brandsSupplied: '',
  moq: '',
  unitCasePack: '',
  pricePerUnit: null,
  paymentTerms: '',
  customLogoBranding: 'No',
  lastOrderDate: '',
  lastPurchaseAmount: null,
  status: 'Active',
  notes: '',
};

const headerCellSx = {
  color: '#fff',
  fontWeight: 700,
  fontSize: '0.8rem',
  letterSpacing: '0.04em',
  textTransform: 'uppercase' as const,
  border: '2px solid #7f0000',
  py: 1.5,
  px: 1.5,
  whiteSpace: 'nowrap' as const,
};

const statusColors: Record<string, 'success' | 'default' | 'warning'> = {
  Active: 'success',
  Inactive: 'default',
  Pending: 'warning',
};

const VendorsPage: React.FC = () => {
  const { showSuccess, showError } = useSnackbar();
  const { sessionToken, user } = useAuth();
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState<Vendor | null>(null);
  const [form, setForm] = useState<CreateVendorRequest>(emptyForm);
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'pending' | 'saving' | 'saved' | 'error'>('idle');
  const [autoSaveError, setAutoSaveError] = useState('');
  const autoSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    };
  }, []);

  const isAdmin = user?.type === 'admin';

  const fetchVendors = async () => {
    if (!sessionToken) return;
    setLoading(true);
    try {
      const res = await vendorsApi.getAll({ page: 1, limit: 500 }, sessionToken);
      setVendors(res.data);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendors();
  }, [sessionToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const openAdd = () => {
    setEditingVendor(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (vendor: Vendor) => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
    }
    setAutoSaveStatus('idle');
    setAutoSaveError('');
    setEditingVendor(vendor);
    setForm({
      companyName: vendor.companyName || '',
      vendorCategory: vendor.vendorCategory || '',
      contactPerson: vendor.contactPerson || '',
      country: vendor.country || '',
      state: vendor.state || '',
      city: vendor.city || '',
      address: vendor.address || '',
      phone: vendor.phone || '',
      email: vendor.email || '',
      website: vendor.website || '',
      productsSupplied: vendor.productsSupplied || '',
      productCategory: vendor.productCategory || '',
      manufacturerDistributor: vendor.manufacturerDistributor || '',
      brandsSupplied: vendor.brandsSupplied || '',
      moq: vendor.moq || '',
      unitCasePack: vendor.unitCasePack || '',
      pricePerUnit: vendor.pricePerUnit,
      paymentTerms: vendor.paymentTerms || '',
      customLogoBranding: vendor.customLogoBranding || 'No',
      lastOrderDate: vendor.lastOrderDate ? vendor.lastOrderDate.slice(0, 10) : '',
      lastPurchaseAmount: vendor.lastPurchaseAmount,
      status: vendor.status || 'Active',
      notes: vendor.notes || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (!sessionToken || !window.confirm('Delete this vendor?')) return;
    try {
      await vendorsApi.remove(id, sessionToken);
      showSuccess('Vendor deleted.');
      fetchVendors();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete vendor');
    }
  };

  const validateForm = (f: CreateVendorRequest): string | null => {
    if (!f.companyName.trim()) {
      return 'Vendor / Company Name is required.';
    }
    return null;
  };

  const buildPayload = (f: CreateVendorRequest): Partial<CreateVendorRequest> => ({
    ...f,
    companyName: f.companyName.trim(),
    pricePerUnit: f.pricePerUnit === null || (f.pricePerUnit as any) === '' ? null : Number(f.pricePerUnit),
    lastPurchaseAmount: f.lastPurchaseAmount === null || (f.lastPurchaseAmount as any) === '' ? null : Number(f.lastPurchaseAmount),
    lastOrderDate: f.lastOrderDate || undefined,
    paymentTerms: f.paymentTerms || undefined,
  });

  // Auto-saves ~900ms after the last change to any field, while editing an existing
  // vendor. Adding a new vendor still requires the explicit Add button.
  const performAutoSave = useCallback(
    async (id: number, snapshot: CreateVendorRequest) => {
      if (!sessionToken) return;
      const validationError = validateForm(snapshot);
      if (validationError) {
        setAutoSaveStatus('error');
        setAutoSaveError(validationError);
        return;
      }
      setAutoSaveStatus('saving');
      try {
        await vendorsApi.update(id, buildPayload(snapshot), sessionToken);
        setAutoSaveStatus('saved');
        fetchVendors();
      } catch (err) {
        setAutoSaveStatus('error');
        setAutoSaveError(err instanceof Error ? err.message : 'Failed to save changes');
      }
    },
    [sessionToken]
  );

  const handleFieldChange = <K extends keyof CreateVendorRequest>(field: K, value: CreateVendorRequest[K]) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (editingVendor) {
        if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
        setAutoSaveStatus('pending');
        autoSaveTimeoutRef.current = setTimeout(() => {
          autoSaveTimeoutRef.current = null;
          performAutoSave(editingVendor.id, next);
        }, 900);
      }
      return next;
    });
  };

  const handleCloseDialog = async () => {
    if (autoSaveTimeoutRef.current && editingVendor) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
      await performAutoSave(editingVendor.id, form);
    }
    setDialogOpen(false);
  };

  const handleSave = async () => {
    if (!sessionToken) return;
    const validationError = validateForm(form);
    if (validationError) {
      showError(validationError);
      return;
    }

    setSaving(true);
    try {
      const payload = buildPayload(form);
      if (editingVendor) {
        await vendorsApi.update(editingVendor.id, payload, sessionToken);
        showSuccess('Vendor updated.');
      } else {
        await vendorsApi.create(payload, sessionToken);
        showSuccess('Vendor added.');
      }
      setDialogOpen(false);
      fetchVendors();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to save vendor');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (d: string | null) =>
    d ? new Date(d.slice(0, 10) + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            You do not have permission to view this page.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" sx={{ color: "#7f0000", fontWeight: 700, flex: "0 0 auto", whiteSpace: "nowrap" }}>
          Date: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
        </Typography>
        <Typography variant="h4" component="h1" sx={{ flex: 1, textAlign: 'center', fontWeight: 700, textTransform: 'uppercase' }}>
          Vendor Information
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openAdd}
          sx={{ backgroundColor: '#b71c1c', '&:hover': { backgroundColor: '#7f0000' } }}
        >
          Add Vendor
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {vendors.length} vendor{vendors.length !== 1 ? 's' : ''}
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ width: '100%', overflow: 'hidden', border: '2px solid #9e9e9e' }}>
          <TableContainer sx={{ maxWidth: '100%' }}>
            <Table sx={{ borderCollapse: 'collapse', '& td': { border: '2px solid #9e9e9e', py: 1.25, px: 1.5 } }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#b71c1c' }}>
                  <TableCell sx={headerCellSx}>Vendor ID</TableCell>
                  <TableCell sx={headerCellSx}>Company Name</TableCell>
                  <TableCell sx={headerCellSx}>Category</TableCell>
                  <TableCell sx={headerCellSx}>Contact Person</TableCell>
                  <TableCell sx={headerCellSx}>Phone</TableCell>
                  <TableCell sx={headerCellSx}>Email</TableCell>
                  <TableCell sx={headerCellSx}>Location</TableCell>
                  <TableCell sx={headerCellSx}>Payment Terms</TableCell>
                  <TableCell sx={headerCellSx}>Last Order Date</TableCell>
                  <TableCell sx={headerCellSx}>Status</TableCell>
                  <TableCell align="center" sx={headerCellSx}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vendors.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 5, border: '2px solid #9e9e9e', color: 'text.secondary' }}>
                      No vendors yet. Click "Add Vendor" to get started.
                    </TableCell>
                  </TableRow>
                )}
                {vendors.map((vendor, idx) => (
                  <TableRow
                    key={vendor.id}
                    sx={{
                      backgroundColor: idx % 2 === 0 ? '#fff' : '#fdf5f5',
                      '&:hover': { backgroundColor: '#fce4e4' },
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{vendor.id}</Typography>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{vendor.companyName}</TableCell>
                    <TableCell>{vendor.vendorCategory || '—'}</TableCell>
                    <TableCell>{vendor.contactPerson || '—'}</TableCell>
                    <TableCell>{formatPhoneNumber(vendor.phone) || '—'}</TableCell>
                    <TableCell>{vendor.email || '—'}</TableCell>
                    <TableCell>{[vendor.city, vendor.state, vendor.country].filter(Boolean).join(', ') || '—'}</TableCell>
                    <TableCell>{vendor.paymentTerms || '—'}</TableCell>
                    <TableCell>{formatDate(vendor.lastOrderDate)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={vendor.status} color={statusColors[vendor.status] || 'default'} />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Edit">
                        <IconButton size="small" color="primary" onClick={() => openEdit(vendor)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => handleDelete(vendor.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingVendor ? 'Edit Vendor' : 'Add Vendor'}</DialogTitle>
        <DialogContent sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Vendor / Company Name"
                fullWidth
                required
                value={form.companyName}
                onChange={e => handleFieldChange('companyName', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Vendor Category"
                fullWidth
                value={form.vendorCategory}
                onChange={e => handleFieldChange('vendorCategory', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Contact Person"
                fullWidth
                value={form.contactPerson}
                onChange={e => handleFieldChange('contactPerson', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Phone"
                fullWidth
                value={form.phone}
                onChange={e => handleFieldChange('phone', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                type="email"
                fullWidth
                value={form.email}
                onChange={e => handleFieldChange('email', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Website"
                fullWidth
                value={form.website}
                onChange={e => handleFieldChange('website', e.target.value)}
              />
            </Grid>

            <Grid item xs={12}><Divider sx={{ my: 1 }}>Location</Divider></Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                label="Country"
                fullWidth
                value={form.country}
                onChange={e => handleFieldChange('country', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="State / Province"
                fullWidth
                value={form.state}
                onChange={e => handleFieldChange('state', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="City"
                fullWidth
                value={form.city}
                onChange={e => handleFieldChange('city', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Address"
                fullWidth
                multiline
                minRows={2}
                value={form.address}
                onChange={e => handleFieldChange('address', e.target.value)}
              />
            </Grid>

            <Grid item xs={12}><Divider sx={{ my: 1 }}>Products</Divider></Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Products Supplied"
                fullWidth
                multiline
                minRows={2}
                value={form.productsSupplied}
                onChange={e => handleFieldChange('productsSupplied', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Product Category"
                fullWidth
                value={form.productCategory}
                onChange={e => handleFieldChange('productCategory', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Manufacturer / Distributor"
                fullWidth
                value={form.manufacturerDistributor}
                onChange={e => handleFieldChange('manufacturerDistributor', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Brands Supplied"
                fullWidth
                value={form.brandsSupplied}
                onChange={e => handleFieldChange('brandsSupplied', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Minimum Order Quantity (MOQ)"
                fullWidth
                value={form.moq}
                onChange={e => handleFieldChange('moq', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Unit / Case Pack"
                fullWidth
                value={form.unitCasePack}
                onChange={e => handleFieldChange('unitCasePack', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Price / Unit"
                type="number"
                fullWidth
                inputProps={{ min: 0, step: 0.01 }}
                InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>$</Typography> }}
                value={form.pricePerUnit ?? ''}
                onChange={e => handleFieldChange('pricePerUnit', e.target.value === '' ? null : Number(e.target.value))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Payment Terms</InputLabel>
                <Select
                  label="Payment Terms"
                  value={form.paymentTerms || ''}
                  onChange={e => handleFieldChange('paymentTerms', e.target.value)}
                >
                  <MenuItem value=""><em>None</em></MenuItem>
                  {PAYMENT_TERMS_OPTIONS.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Custom Logo / Branding</InputLabel>
                <Select
                  label="Custom Logo / Branding"
                  value={form.customLogoBranding || 'No'}
                  onChange={e => handleFieldChange('customLogoBranding', e.target.value as 'Yes' | 'No')}
                >
                  {YES_NO_OPTIONS.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}><Divider sx={{ my: 1 }}>Order History & Status</Divider></Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                label="Last Order Date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.lastOrderDate}
                onChange={e => handleFieldChange('lastOrderDate', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Last Purchase Amount"
                type="number"
                fullWidth
                inputProps={{ min: 0, step: 0.01 }}
                InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>$</Typography> }}
                value={form.lastPurchaseAmount ?? ''}
                onChange={e => handleFieldChange('lastPurchaseAmount', e.target.value === '' ? null : Number(e.target.value))}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={form.status || 'Active'}
                  onChange={e => handleFieldChange('status', e.target.value as 'Active' | 'Inactive' | 'Pending')}
                >
                  {STATUS_OPTIONS.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Notes"
                fullWidth
                multiline
                minRows={2}
                value={form.notes}
                onChange={e => handleFieldChange('notes', e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={editingVendor ? { justifyContent: 'space-between' } : undefined}>
          {editingVendor ? (
            <>
              <Typography
                variant="body2"
                color={autoSaveStatus === 'error' ? 'error' : 'text.secondary'}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1 }}
              >
                {autoSaveStatus === 'saving' && (<><CircularProgress size={14} />Saving…</>)}
                {autoSaveStatus === 'pending' && 'Unsaved changes…'}
                {autoSaveStatus === 'saved' && 'All changes saved'}
                {autoSaveStatus === 'error' && (autoSaveError || 'Failed to save changes')}
              </Typography>
              <Button onClick={handleCloseDialog} variant="contained" disabled={autoSaveStatus === 'saving'}
                sx={{ backgroundColor: '#b71c1c', '&:hover': { backgroundColor: '#7f0000' } }}>
                Close
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleCloseDialog} disabled={saving}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                sx={{ backgroundColor: '#b71c1c', '&:hover': { backgroundColor: '#7f0000' } }}
              >
                {saving ? <CircularProgress size={20} color="inherit" /> : 'Add'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VendorsPage;
